import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { z } from 'zod';
import { atomWithStaleRevalidate } from '../core/storage';
import { FocusResa } from '../core/focusResa';
import { locationReserationsAtom } from './reservation';
import { focusResaAtom, rbcLocalizerAtom } from './core';

export const locationTagParser = z.object({
	name: z.string(),
	taxonomy: z.string(),
	slug: z.string(),
});
export type LocationTag = z.infer<typeof locationTagParser>;

export const locationParser = z.object({
	ID: z.number(),
	post_title: z.string(),
	confirmed: z.coerce.boolean(),
	isPublic: z.coerce.boolean(),
	emoji: z.string().nullable().optional(),
	openHours: z
		.array(
			z.object({
				start: z.coerce.date(),
				end: z.coerce.date(),
				capacity: z.number().default(-1),
			}),
		)
		.nullable()
		.default([]),
	coordinates: z.string().nullable(),
	environments: z.array(locationTagParser),
	distance: locationTagParser.nullable(),
	requiresApproval: z.coerce.boolean(),
	url: z.string(),
});

export type ResaLocation = z.infer<typeof locationParser>;

export const mergeLocations = (prev: ResaLocation[], next: ResaLocation[]) => {
	return [...prev.filter((l) => !next.some((l2) => l2.ID == l.ID)), ...next];
};

export const locationAtom = atomFamily((id: number) =>
	atom(
		(get) => {
			if (id == -1) return get(tbdLocationAtom);
			return get(allLocationsAtom).find((l) => l.ID == id);
		},
		(get, set, value: ResaLocation) => {
			set(allLocationsAtom, (locations) => [
				...locations.filter((l) => l.ID != id),
				value,
			]);
		},
	),
);

export const tbdLocationAtom = atom<ResaLocation>((get) => ({
	ID: -1,
	post_title: 'Autre / À Définir',
	confirmed: true,
	isPublic: true,
	emoji: '',
	openHours: [{ ...get(focusResaAtom).dates, capacity: -1 }],
	coordinates: null,
	environments: [],
	distance: null,
	requiresApproval: false,
	url: '',
}));

export const allLocationsAtom = atomWithStaleRevalidate<ResaLocation[]>(
	'focus-resa-locations',
	async (lastUpdate: number) =>
		(!lastUpdate || Date.now() - lastUpdate > 10 * 60 * 1000) &&
		FocusResa.getInstance().getLocations(),
	(_, next) => next,
	[],
);

export const isInOpenHours = (
	e: Date | { start: Date; end: Date },
	openHours: { start: Date; end: Date }[],
): boolean => {
	if ('start' in e)
		return openHours.some((o) => e.start >= o.start && e.end <= o.end);
	return openHours.some((o) => e >= o.start && e <= o.end);
};

// returns a Map with timestamps as key and percentage as value
// each key represent a 30 minute span starting at the timestamp
export const locationFrequentation = atomFamily((locationId: number) =>
	atom((get): Map<number, number> => {
		const location = get(locationAtom(locationId));
		if (location == null) return new Map();

		const inc = 30 * 60 * 1000; // 30 min
		const localizer = get(rbcLocalizerAtom);
		const makeFreqMap = <T>(start: Date, end: Date, initialValue: T) => {
			const freq = new Map<number, T>();
			const s = localizer.startOf(start, 'day').getTime();
			const e = localizer.endOf(end, 'day').getTime();
			for (
				let t = s;
				t < e;
				t += inc // 30 minute increments from start of first day to end of last day
			)
				freq.set(t, initialValue);
			return freq;
		};

		if (
			// location.isPublic ||
			location.openHours == null ||
			location.openHours.length == 0
		) {
			// console.log("early exit", location.isPublic, location.openHours == null, location.openHours?.length == 0)
			const focusResa = get(focusResaAtom);
			return makeFreqMap(focusResa.dates.start, focusResa.dates.end, -1);
		}

		const min = location.openHours.reduce(
			(min, r) => (min ? localizer.min(min, r.start) : r.start),
			null,
		);
		const max = location.openHours.reduce(
			(max, r) => (max ? localizer.max(max, r.end) : r.end),
			null,
		);
		if (!min || !max)
			throw new Error(
				'Unexpected, location has openHours but could not determine first and last',
			);

		const freq = location.openHours.reduce(
			(freq, oh) => {
				const s = oh.start.getTime();
				const e = oh.end.getTime();
				freq.forEach((count, t) => {
					if (s < t + inc && e > t) freq.set(t, [0, oh.capacity]); // if capacity changes during the 30 min slot, the later capacity is used
				});

				return freq;
			},
			makeFreqMap(min, max, [0, 0]),
		);

		get(locationReserationsAtom(locationId))
			.filter((r) => r.anonymized || r.canceledBy == null)
			.forEach((resa) => {
				const s = resa.start.getTime();
				const e = resa.end.getTime();
				freq.forEach((data, t) => {
					if (s < t + inc && e > t) {
						data[0] += 1;
					}
				});

				return freq;
			});

		return new Map(
			Array.from(freq.entries()).map(([t, [count, cap]]) => [
				t,
				cap < 0 ? 0 : cap == 0 ? -1 : count / cap,
			]),
		);
	}),
);
